/**
 * Tool Service - Handles API interactions for agent tools and integrations
 */

import axios from 'axios';

import { get, post, put } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/mms_persistence/tools`;
const TOOL_API = `/api/v1/mms_ai_configs/tool/config`;


/**
 * Get all tools with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of tools
 */
export const getTools = async (filter = {}, sort = {}, query= {}) => {
  try {
    const {skip, limit} = query;
    const response = await post(`${TOOL_API}/fetch`, { filter, sort, skip, limit });
    return response;
  } catch (error) {
    console.error('Error fetching tools:', error);
    throw error;
  }
};
export const getBuiltinTools = async () => {
  try {
    const response = await getTools(
      { tool_type: "builtin" },
      {},
      { skip: 0, limit: 100 }
    );

    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Error fetching builtin tools:", error);
    throw error;
  }
};

/**
 * Get all available tool types from backend
 * @returns {Promise<Array>} Promise resolving to array of tool types
 */
export const getToolTypes = async () => {
  try {
    const response = await get(`${TOOL_API}/types`);
    return response;
  } catch (error) {
    console.error('Error fetching tool types:', error);
    throw error;
  }
};

/**
 * Get a specific tool by ID
 * @param {string} toolId - Tool ID
 * @returns {Promise<Object>} Promise resolving to tool object
 */
export const getToolById = async (toolId) => {
  try {
    const response = await get(`${TOOL_API}/get_by_id/${toolId}`);
    return response;
  } catch (error) {
    console.error(`Error fetching tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Create a new tool
 * @param {Object} toolData - Tool data
 * @returns {Promise<Object>} Promise resolving to created tool
 */
export const createTool = async (toolData) => {
  try {
    const response = await post(TOOL_API, toolData);
    return response;
  } catch (error) {
    console.error('Error creating tool:', error);
    throw error;
  }
};


/**
 * Update an existing tool
 * @param {string} toolId - Tool ID
 * @param {Object} toolData - Updated tool data
 * @returns {Promise<Object>} Promise resolving to updated tool
 */
export const updateTool = async (toolId, toolData) => {
  try {
    const response = await put(TOOL_API, toolData);
    return response;
  } catch (error) {
    console.error(`Error updating tool ${toolId}:`, error);
    throw error;
  }
};


/**
 * Delete a tool
 * @param {string} toolId - Tool ID
 * @returns {Promise<void>} Promise resolving when tool is deleted
 */
export const deleteTool = async (toolId) => {
  try {
    await axios.delete(`${API_URL}/${toolId}`);
  } catch (error) {
    console.error(`Error deleting tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Get tool categories
 * @returns {Promise<Array>} Promise resolving to array of tool categories
 */
export const getToolCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tool categories:', error);
    throw error;
  }
};

/**
 * Get tool permissions
 * @param {string} toolId - Tool ID
 * @returns {Promise<Array>} Promise resolving to array of permissions
 */
export const getToolPermissions = async (toolId) => {
  try {
    const response = await axios.get(`${API_URL}/${toolId}/permissions`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching permissions for tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Update tool permissions
 * @param {string} toolId - Tool ID
 * @param {Array} permissions - Updated permissions
 * @returns {Promise<Object>} Promise resolving to updated tool
 */
export const updateToolPermissions = async (toolId, permissions) => {
  try {
    const response = await axios.put(`${API_URL}/${toolId}/permissions`, { permissions });
    return response.data;
  } catch (error) {
    console.error(`Error updating permissions for tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Execute a tool
 * @param {string} toolId - Tool ID
 * @param {Object} params - Execution parameters
 * @returns {Promise<Object>} Promise resolving to execution result
 */
export const executeTool = async (toolId, params = {}) => {
  try {
    const response = await axios.post(`${API_URL}/${toolId}/execute`, params);
    return response.data;
  } catch (error) {
    console.error(`Error executing tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Get tool execution history
 * @param {string} toolId - Tool ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of execution history
 */
export const getToolExecutionHistory = async (toolId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${toolId}/execution-history`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching execution history for tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Get tool configurations
 * @param {string} toolId - Tool ID
 * @returns {Promise<Object>} Promise resolving to tool configurations
 */
export const getToolConfigurations = async (toolId) => {
  try {
    const response = await axios.get(`${API_URL}/${toolId}/configurations`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching configurations for tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Update tool configurations
 * @param {string} toolId - Tool ID
 * @param {Object} configurations - Updated configurations
 * @returns {Promise<Object>} Promise resolving to updated tool
 */
export const updateToolConfigurations = async (toolId, configurations) => {
  try {
    const response = await axios.put(`${API_URL}/${toolId}/configurations`, configurations);
    return response.data;
  } catch (error) {
    console.error(`Error updating configurations for tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Get tool integrations
 * @param {string} toolId - Tool ID
 * @returns {Promise<Array>} Promise resolving to array of integrations
 */
export const getToolIntegrations = async (toolId) => {
  try {
    const response = await axios.get(`${API_URL}/${toolId}/integrations`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching integrations for tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Add tool integration
 * @param {string} toolId - Tool ID
 * @param {Object} integration - Integration data
 * @returns {Promise<Object>} Promise resolving to created integration
 */
export const addToolIntegration = async (toolId, integration) => {
  try {
    const response = await axios.post(`${API_URL}/${toolId}/integrations`, integration);
    return response.data;
  } catch (error) {
    console.error(`Error adding integration to tool ${toolId}:`, error);
    throw error;
  }
};

/**
 * Remove tool integration
 * @param {string} toolId - Tool ID
 * @param {string} integrationId - Integration ID
 * @returns {Promise<void>} Promise resolving when integration is removed
 */
export const removeToolIntegration = async (toolId, integrationId) => {
  try {
    await axios.delete(`${API_URL}/${toolId}/integrations/${integrationId}`);
  } catch (error) {
    console.error(`Error removing integration ${integrationId} from tool ${toolId}:`, error);
    throw error;
  }
};

export const testEmailConnection = async (emailConfig) => {
  try {
    const response = await post(`${TOOL_API}/test-email`, emailConfig);
    return response;
  } catch (error) {
    console.error('Error testing email connection:', error);
    throw error;
  }
};

export default {
  getTools,
  getToolTypes,
  getBuiltinTools,
  getToolById,
  createTool,
  updateTool,
  deleteTool,
  getToolCategories,
  getToolPermissions,
  updateToolPermissions,
  executeTool,
  getToolExecutionHistory,
  getToolConfigurations,
  updateToolConfigurations,
  getToolIntegrations,
  addToolIntegration,
  removeToolIntegration,
  testEmailConnection
};

